import React, { lazy, useEffect, useMemo, useRef } from 'react';

import Image from '../../../../library/image/image';
import Typography from '../../../../library/typography/typography';
import { ProductListPage } from '../../../../routes/page-routes/page-routes-imports';
import LoadingSpinner from '../../../loading-spinner/loading-spinner';
import MyAccountModal from '../../../my-account-modal/my-account-modal';

import {
  getFitmentStaggeredTireProductSearch,
  getFitmentTireProductSearch
} from '../../../../shared/graphql/product-list-queries';

import {
  useFitment,
  useGraphQLClient,
  useMyStore,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import {
  getVehicleInput,
  parseTireSize
} from '../../../../shared/utils/fitment/fitment';
import { formatToTitleCase } from '../../../../shared/utils/format/format';
import { localStorage } from '../../../../shared/utils/storage/storage';
import { getTreadwellGQLInput } from '../../../../shared/utils/treadwell/treadwell';

import { TireSearchOption } from '../../../../shared/types/graphql-types';
import { STAGGERED_SEARCH_TYPES } from '../../../../shared/constants/staggered-products';
import { TREADWELL_VEHICLE_PREFERENCES } from '../../../../shared/constants/storage-keys';

import './show-results-modal.scss';

interface ShowResultsModalProps {
  onClose: () => void;
}

function ShowResultsModal(props: ShowResultsModalProps) {
  const { onClose } = props;

  const { preferences } = localStorage(TREADWELL_VEHICLE_PREFERENCES);

  const {
    isCustomDriverTypeShowing,
    selectedCustomDrivingPrioritiesType,
    selectedDriverType,
    zipCodeValue
  } = preferences || {};

  const timerRef: { current: NodeJS.Timeout | null } = useRef(null);
  const { isFitmentLoading, selectedFitment } = useFitment();
  const { treadwellPreferences } = useTreadwellPreferences();
  const client = useGraphQLClient();
  const treadwell = getTreadwellGQLInput(selectedFitment, treadwellPreferences);
  const { myStore, nearbyStorePreferences } = useMyStore();
  const personaDriverType = selectedDriverType?.name || '';

  const vehicleInput = useMemo(
    () => getVehicleInput(selectedFitment),
    [selectedFitment]
  );

  const frontTireSize = parseTireSize(vehicleInput?.frontTireSize);
  const rearTireSize = parseTireSize(vehicleInput?.rearTireSize);
  const searchQuery = treadwellPreferences?.selectedInStockTires
    ? ':recommended:storeInStock:In Stock'
    : ':recommended';

  useEffect(() => {
    if (typeof timerRef.current !== 'number') {
      timerRef.current = setInterval(() => {
        onClose();
      }, 6000);
    }
  }, [onClose]);

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      lazy(ProductListPage);
    },
    []
  );

  useEffect(() => {
    const productSearch = {
      frontTireSize: frontTireSize,
      nearByStoreCodes: nearbyStorePreferences,
      pageNum: 0,
      searchOption: TireSearchOption.TreadWell,
      searchQuery: searchQuery,
      storeCode: myStore && myStore.code,
      treadwell: treadwell,
      vehicleInput: vehicleInput
    };

    if (!isFitmentLoading && frontTireSize && treadwell) {
      if (selectedFitment.isStaggered) {
        const productSearchStaggered = getFitmentStaggeredTireProductSearch({
          ...productSearch,
          rearTireSize: rearTireSize,
          staggeredSearchType: STAGGERED_SEARCH_TYPES.SET
        });

        client.query(productSearchStaggered);
      } else {
        client.query(getFitmentTireProductSearch(productSearch));
      }
    }
  }, [
    client,
    frontTireSize,
    isFitmentLoading,
    myStore,
    nearbyStorePreferences,
    rearTireSize,
    searchQuery,
    selectedFitment,
    selectedFitment.isStaggered,
    treadwell,
    vehicleInput
  ]);

  return (
    <MyAccountModal
      isOpen
      onClose={onClose}
      showTreadwellBanner
      styleName="show-results-interstitial-modal"
    >
      <div styleName="container">
        <div styleName="tready-image">
          <Image
            alt="Searching Treadwell for Results"
            height="136"
            src="https://discounttire.scene7.com/is/content/discounttire/logos/treadwell_icon.svg"
            width="136"
          />
        </div>

        {preferences && (
          <Typography styleName="tready-message" tag="span" weight="black">
            Treadwell is finding the best tire matches for you based on&nbsp;
            your vehicle, where you drive {`(${zipCodeValue})`} and your&nbsp;
            driver type&nbsp;
            {`(${
              !isCustomDriverTypeShowing && selectedDriverType
                ? formatToTitleCase(personaDriverType)
                : formatToTitleCase(selectedCustomDrivingPrioritiesType)
            })`}
            !
          </Typography>
        )}

        <LoadingSpinner />
      </div>
    </MyAccountModal>
  );
}

export default ShowResultsModal;
